/**
 * HeroSideHud.js
 * Implements the Hero side HUD
 */

import {
    Game,
    GameTween,
    CoreEvents,
    UiElement,
    UiButton,
    UiImage,
    UiText,
    GameAnimatedSprite,
    KeyboardKey,
    UiFrame,
    GameSprite,
    GameText,
    GameShape,
    GameTimer
} from "pixi_core";

import { Hero } from "../Heroes/Hero";
import { Common } from "../game/Common";
import { EnemyParticle } from "../game/particles/EnemyParticle";

import {
    SceneLayers,
    GameEvents,
    CheatEvents,
    Tutorials,
    BuffDebuff,
    TEAM_SIZE,
    HERO_GAMEPLAY_SPRITES,
    HUD_HERO_BOX_WIDTH,
    HUD_HERO_BOX_HEIGHT,
    HUD_HERO_BOX_BOTTOM,
    HERO_ABILITY_SPRITE,
    HERO_DAMAGED_SPRITES,
    HP_BAR_DAMAGE_ANIMATION,
    HERO_HEALING_ANIMATION,
    HERO_POWER_UP_BUFF_ANIMATION,
    ENEMY_ATTACK_PARTICLE_TARGET_X,
    HUD_HERO_BOX_CHARGE_ANIMATION_DURATION,
    TUTORIAL_ABILITY_HAND_OFFSET,
    HERO_DAMAGE_LABEL_POSITION
} from "../Constants";

export class HeroSideHud {
    /**
     * @public
     * @param {UiElement} parent
     */
    constructor(parent) {
        Common.hudHeroes = this;

        /** @private @type {UiElement} */
        this._ui = Game.ui.instantiate("Heroes", parent);

        /** @private @type {Boolean} */
        this._inputsEnabled = true;

        /** @private @type {Hero[]} */
        this._heroes = [];

        /** @private  @type {Number} */
        this._maxHP = 0;

        /** @private  @type {Number} */
        this._targetHP = 0;

        /** @private  @type {Number} */
        this._displayedHP = 0;

        /** @private @type {Number} */
        this._heroesDamagedTimer = 0;

        /** @private @type {Boolean} */
        this._heroesDamaged = false;

        /** @private @type {Boolean} */
        this._fullScreenAbilityPause = false;

        /** @private @type {UiText} */
        this._heroesLblHP = this._ui.findElementByName("LblHeroHP");

        /** @private @type {GameText} */
        this._txtHeroesDamage = new GameText({
            text: "",
            textStyle: "Font_InGameNumbersDamage"
        });
        this._txtHeroesDamage.visible = false;
        Common.scene.addChild(this._txtHeroesDamage, SceneLayers.ePARTICLES);

        /** @private @type {UiImage} */
        this._captainAmericaShield = this._ui.findElementByName("ImgCaptainAmericaShield");

        /** @private @type {Object} */
        this._captainAmericaShieldPosition = { x: this._captainAmericaShield.x, y: this._captainAmericaShield.y };

        /** @private @type {GameTween} */
        this._tweenCaptainAmericaShield = null;

        /** @private @type {UiImage} */
        this._hpBar = this._ui.findElementByName("ImgHPBar");

        /** @private @type {GameTween} */
        this._tweenDamageMove = null;

        /** @private  @type {Number} */
        this._captainAmericaShieldAppearDisappearTime = 0;

        /** @private @type {Number} */
        this._captainAmericaShieldStartSize = 0;

        /** @private @type {Number} */
        this._captainAmericaShieldFinalSize = 0;

        /** @type {UiButton} */
        const btnHero1 = this._ui.findElementByName("BtnHero1");
        /** @type {UiButton} */
        const btnHero2 = this._ui.findElementByName("BtnHero2");
        /** @type {UiButton} */
        const btnHero3 = this._ui.findElementByName("BtnHero3");
        /** @type {UiButton} */
        const btnHero4 = this._ui.findElementByName("BtnHero4");

        /** @type {UiFrame} */
        const frmHero1 = this._ui.findElementByName("FrmHero1");
        /** @type {UiFrame} */
        const frmHero2 = this._ui.findElementByName("FrmHero2");
        /** @type {UiFrame} */
        const frmHero3 = this._ui.findElementByName("FrmHero3");
        /** @type {UiFrame} */
        const frmHero4 = this._ui.findElementByName("FrmHero4");

        /** @type {UiFrame} */
        const frmAbilityLines = this._ui.findElementByName("AbilityLines");

        /** @type {UiFrame[]} */
        this._frmAbilityLines = [
            frmAbilityLines.findElementByName("Hero1"),
            frmAbilityLines.findElementByName("Hero2"),
            frmAbilityLines.findElementByName("Hero3"),
            frmAbilityLines.findElementByName("Hero4")
        ];

        /** @private @type {UiButton[]} */
        this._heroButtons = [btnHero1, btnHero2, btnHero3, btnHero4];

        /** @private @type {UiFrame[]} */
        this._frmHeroes = [frmHero1, frmHero2, frmHero3, frmHero4];

        /** @private @type {UiFrame} */
        this._frmDarkens = this._ui.findElementByName("Darkens");

        /** @private @type {UiImage[]} */
        this._imgDarkens = [
            this._frmDarkens.findElementByName("Hero1"),
            this._frmDarkens.findElementByName("Hero2"),
            this._frmDarkens.findElementByName("Hero3"),
            this._frmDarkens.findElementByName("Hero4")
        ];

        /** @private @type {GameShape} */
        this._darkenMask = new GameShape();
        this._frmDarkens.uiRoot.addChild(this._darkenMask);
        this._frmDarkens.uiRoot.mask = this._darkenMask;

        const btnAbility1 = frmHero1.findElementByName("BtnAbility");
        const btnAbility2 = frmHero2.findElementByName("BtnAbility");
        const btnAbility3 = frmHero3.findElementByName("BtnAbility");
        const btnAbility4 = frmHero4.findElementByName("BtnAbility");

        /** @private @type {UiImage[]} */
        this._abilityIconsIdle = [
            btnAbility1.findElementByName("IconIdle"),
            btnAbility2.findElementByName("IconIdle"),
            btnAbility3.findElementByName("IconIdle"),
            btnAbility4.findElementByName("IconIdle")
        ];

        /** @private @type {UiImage[]} */
        this._abilityIconsPressed = [
            btnAbility1.findElementByName("IconPressed"),
            btnAbility2.findElementByName("IconPressed"),
            btnAbility3.findElementByName("IconPressed"),
            btnAbility4.findElementByName("IconPressed")
        ];

        /** @private @type {UiButton[]} */
        this._abilityButtons = [btnAbility1, btnAbility2, btnAbility3, btnAbility4];

        /** @private @type {UiImage[]} */
        this._heroPortraits = [
            btnHero1.findElementByName("ImgHero"),
            btnHero2.findElementByName("ImgHero"),
            btnHero3.findElementByName("ImgHero"),
            btnHero4.findElementByName("ImgHero")
        ];

        /** @private @type {Object[]} */
        this._heroButtonsOriginalPos = [];

        for (let i = 0; i < this._heroButtons.length; ++i) {
            this._heroButtonsOriginalPos.push({ x: this._heroButtons[i].x, y: this._heroButtons[i].y });
        }

        /** @private @type {UiText[]} */
        this._cooldownLabels = [
            frmHero1.findElementByName("Cooldown"),
            frmHero2.findElementByName("Cooldown"),
            frmHero3.findElementByName("Cooldown"),
            frmHero4.findElementByName("Cooldown")
        ];

        /** @private @type {UiImage[]} */
        this._checkmarks = [
            frmHero1.findElementByName("Checkmark"),
            frmHero2.findElementByName("Checkmark"),
            frmHero3.findElementByName("Checkmark"),
            frmHero4.findElementByName("Checkmark")
        ];
        for (const checkmark of this._checkmarks) {
            checkmark.visible = false;
        }

        /** @private @type {UiImage[]} */
        this._counterHighlights = [
            frmHero1.findElementByName("CounterHighlight"),
            frmHero2.findElementByName("CounterHighlight"),
            frmHero3.findElementByName("CounterHighlight"),
            frmHero4.findElementByName("CounterHighlight")
        ];

        /** @private @type {UiImage[]} */
        this._abilityHighlights = [
            frmHero1.findElementByName("AbilityHighlight"),
            frmHero2.findElementByName("AbilityHighlight"),
            frmHero3.findElementByName("AbilityHighlight"),
            frmHero4.findElementByName("AbilityHighlight")
        ];
        this._onHighlightClear();

        /** @private @type {GameAnimatedSprite[]} */
        this._abilityLine = [];
        /** @private @type {GameAnimatedSprite[]} */
        this._abilityReady = [];

        /** @private @type {Object[]} */
        this._heroCharge = [
            { ratio: 1, targetRatio: 1, speed: 0 },
            { ratio: 1, targetRatio: 1, speed: 0 },
            { ratio: 1, targetRatio: 1, speed: 0 },
            { ratio: 1, targetRatio: 1, speed: 0 }
        ];

        const abilityLineData = Game.design.getGraphicData("AbilityLine");
        const abilityReadyData = Game.design.getGraphicData("AbilityReady");
        const abilityMaskData = Game.design.getGraphicData("AbilityMask");

        for (let i = 0; i < TEAM_SIZE; ++i) {
            /** @type {UiImage} */
            const heroButtonTop = this._frmHeroes[i].findElementByName("ImgHeroTop");

            const abilityLine = new GameAnimatedSprite(null, abilityLineData);
            abilityLine.visible = false;
            this._abilityLine.push(abilityLine);
            this._frmAbilityLines[i].uiRoot.addChild(abilityLine);

            const abilityReady = new GameAnimatedSprite(null, abilityReadyData);
            heroButtonTop.uiRoot.addChild(abilityReady);
            abilityReady.visible = false;
            this._abilityReady.push(abilityReady);
        }

        const abilityLineMask = new GameSprite(abilityMaskData);
        frmAbilityLines.uiRoot.addChild(abilityLineMask);
        frmAbilityLines.uiRoot.mask = abilityLineMask;

        /** @private @type {UiImage[]} */
        this._imgStunned = [
            frmHero1.findElementByName("ImgStunned"),
            frmHero2.findElementByName("ImgStunned"),
            frmHero3.findElementByName("ImgStunned"),
            frmHero4.findElementByName("ImgStunned")
        ];

        /** @private @type {UiText[]} */
        this._lblStunCounter = [
            frmHero1.findElementByName("LblStunCounter"),
            frmHero2.findElementByName("LblStunCounter"),
            frmHero3.findElementByName("LblStunCounter"),
            frmHero4.findElementByName("LblStunCounter")
        ];

        /** @private @type {GameAnimatedSprite[]} */
        this._healingAnimation = [];

        for (let i = 0; i < this._heroPortraits.length; ++i) {
            let healingAnimation = new GameAnimatedSprite(null, Game.design.getGraphicData(HERO_HEALING_ANIMATION));
            this._healingAnimation.push(healingAnimation);
            healingAnimation.x = this._heroPortraits[i].x;
            healingAnimation.y = this._heroPortraits[i].y;
            this._heroButtons[i].uiRoot.addChild(healingAnimation);
        }

        /** @private @type {GameAnimatedSprite[]} */
        this._powerUpBuffAnimation = [];

        for (let i = 0; i < this._heroPortraits.length; ++i) {
            let powerUpAnimation = new GameAnimatedSprite(null, Game.design.getGraphicData(HERO_POWER_UP_BUFF_ANIMATION));
            this._powerUpBuffAnimation.push(powerUpAnimation);
            powerUpAnimation.x = this._heroPortraits[i].x;
            powerUpAnimation.y = this._heroPortraits[i].y;
            this._heroButtons[i].uiRoot.addChild(powerUpAnimation);
        }

        /** @private @type {UiFrame[]} */
        this._activeBuffFrames = [];

        /** @private @type {Number[]} */
        this._buffFramePosY = [];

        /** @private @type {UiFrame} */
        this._frameBuffAllOut = this._ui.findElementByName("BuffAllOut");
        /** @private @type {UiText} */
        this._textBuffAllOut = this._frameBuffAllOut.findElementByName("Counter");
        this._buffFramePosY.push(this._frameBuffAllOut.y);

        /** @private @type {UiFrame} */
        this._frameBuffCounter = this._ui.findElementByName("BuffCounter");
        /** @private @type {UiText} */
        this._textBuffCounter = this._frameBuffCounter.findElementByName("Counter");
        this._buffFramePosY.push(this._frameBuffCounter.y);

        /** @private @type {UiFrame} */
        this._frameBuffOvertime = this._ui.findElementByName("BuffOvertime");
        /** @private @type {UiText} */
        this._textBuffOvertime = this._frameBuffOvertime.findElementByName("Counter");
        this._buffFramePosY.push(this._frameBuffOvertime.y);

        /** @private @type {UiFrame} */
        this._frameBuffRandomizedShooter = this._ui.findElementByName("BuffRandomizedShooter");
        /** @private @type {UiText} */
        this._textBuffRandomizedShooter = this._frameBuffRandomizedShooter.findElementByName("Counter");
        this._buffFramePosY.push(this._frameBuffRandomizedShooter.y);

        /** @private @type {UiFrame} */
        this._frameBuffShield = this._ui.findElementByName("BuffShield");
        /** @private @type {UiText} */
        this._textBuffShield = this._frameBuffShield.findElementByName("Counter");
        this._buffFramePosY.push(this._frameBuffShield.y);

        /** @private @type {UiFrame} */
        this._frameBuffSizeManipulation = this._ui.findElementByName("BuffSizeManipulation");
        /** @private @type {UiText} */
        this._textBuffSizeManipulation = this._frameBuffSizeManipulation.findElementByName("Counter");
        this._buffFramePosY.push(this._frameBuffSizeManipulation.y);

        /** @private @type {GameTween[]} */
        this._buffAppearTweens = [];

        /** @private @type {GameTween[]} */
        this._buffDisappearTweens = [];

        /** @private @type {GameTween[]} */
        this._buffMoveTweens = [];

        /** @private @type {GameTimer} */
        this._timerAbilityDelay = null;

        this._damageAnimation = new GameAnimatedSprite(
            null,
            Game.design.getGraphicData(HP_BAR_DAMAGE_ANIMATION),
            Common.scene,
            SceneLayers.ePARTICLES
        );
        this._damageAnimation.visible = false;

        /** @protected @type {Boolean} */
        this._cheatInfiniteAbility = Game.windows.cheatsMenu.getValue("AvengersInfiniteAbility");
    }

    /**
     * @public
     */
    beginLevel() {
        Game.on(GameEvents.eHEROES_INITIALIZED, this._onHeroesInitialized, this);

        Game.on(GameEvents.eHERO_BUTTON_USE_ABILITY, this._onBtnUseAbility, this);
        Game.on(GameEvents.eHERO_INFO_ABILITY, this._onInfoAbility, this);
        Game.on(GameEvents.eHERO_ABILITY_CHARGING, this._onHeroAbilityCharging, this);
        Game.on(GameEvents.eHERO_ABILITY_CHARGED, this._onHeroAbilityCharged, this);
        Game.on(GameEvents.eHEROES_DAMAGED, this._onHeroesDamaged, this);
        Game.on(GameEvents.eHEROES_HEALED, this._onHealHeroes, this);
        Game.on(GameEvents.eHEROES_STUNNED_VISUAL, this._onHeroesStunned, this);
        Game.on(GameEvents.eHEROES_REMOVE_STUN, this._onRemoveStun, this);
        Game.on(GameEvents.eGAME_ENABLE_INPUTS, this._onEnableInputs, this);
        Game.on(GameEvents.eGAME_DISABLE_INPUTS, this._onDisableInputs, this);
        Game.on(GameEvents.eHERO_ABILITY_CAPTAIN_AMERICA, this._onCaptainAmericaShield, this);
        Game.on(GameEvents.eHERO_ABILITY_CAPTAIN_AMERICA_DONE, this._onCaptainAmericaShieldDone, this);
        Game.on(GameEvents.eHEROES_ALL_OUT_ATTACK_CUTIN_DONE, this._onSetAllOutBuff, this);
        Game.on(GameEvents.eHERO_ABILITY_WASP, this._onSetWaspBuff, this);
        Game.on(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ACTIVATE, this._onSpawnCounter, this);
        Game.on(GameEvents.eGAME_STOP_POWER_UP_BUFF_ANIMATION, this._onPowerUpBuffAnimationDone, this);
        Game.on(GameEvents.eGAME_SET_NUMBER_OVERTIME_TURNS, this._onSetOvertimeTurns, this);
        Game.on(GameEvents.eGAME_SET_RANDOM_BUBBLES_COUNTER, this._onSetRandomBubblesCounter, this);
        Game.on(GameEvents.eGAME_SET_BUFF_TIMER, this._onSetBuffTimer, this);
        Game.on(CoreEvents.eINPUT_KEY_DOWN, this._onKeyDown, this);

        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_HERO_COUNTERS, this._onHighlightHeroCounters, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_HERO_ABILITIES, this._onHighlightHeroAbilities, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR, this._onHighlightClear, this);

        Game.on(CheatEvents.eAVENGERS_INFINITE_ABILITY, this._onCheatsAvengersInfiniteAbility, this);
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        if (this._heroesDamaged) {
            this._shakeHeroes(deltaTime);
        }

        if (this._displayedHP !== this._targetHP) {
            this._updateHPBar(deltaTime);
        }

        if (Game.isPaused()) {
            return;
        }

        let redraw = false;
        for (let i = 0; i < TEAM_SIZE; ++i) {
            const charge = this._heroCharge[i];
            if (charge.targetRatio !== charge.ratio) {
                redraw = true;

                charge.ratio += charge.speed * deltaTime;
                if (charge.ratio <= charge.targetRatio) {
                    charge.ratio = charge.targetRatio;
                }

                if (charge.ratio === 0) {
                    this._onAbilityReady(i);
                }

                const abilityLine = this._abilityLine[i];
                abilityLine.y = HUD_HERO_BOX_BOTTOM - HUD_HERO_BOX_HEIGHT * charge.ratio;
            }
        }
        if (redraw) {
            this._drawHeroChargeDarken();
        }
    }

    /**
     * @private
     * @param {Hero[]} heroes
     * @param {Number} totalHp
     */
    _onHeroesInitialized(heroes, totalHp) {
        this._fullScreenAbilityPause = false;
        this._heroes = heroes;
        this._captainAmericaShield.setVisible(false);

        for (let i = 0; i < TEAM_SIZE; ++i) {
            const hero = this._heroes[i];
            const lblCooldown = this._cooldownLabels[i];
            const abilityIconIdle = this._abilityIconsIdle[i];
            const abilityIconPressed = this._abilityIconsPressed[i];
            const checkmark = this._checkmarks[i];

            const abilityLine = this._abilityLine[i];
            const abilityReady = this._abilityReady[i];
            abilityLine.visible = false;
            abilityReady.visible = false;

            this._heroPortraits[i].setSprite(HERO_GAMEPLAY_SPRITES[hero.name]);
            this._heroButtons[i].setSprite("FRM_HeroesBottom");
            abilityIconIdle.setSprite(HERO_ABILITY_SPRITE[hero.name]);
            abilityIconPressed.setSprite(HERO_ABILITY_SPRITE[hero.name]);

            this._healingAnimation[i].visible = false;
            this._powerUpBuffAnimation[i].visible = false;

            lblCooldown.text = (hero.abilityCooldown - hero.charge).toString();
            lblCooldown.visible = !this._cheatInfiniteAbility;
            checkmark.visible = this._cheatInfiniteAbility;

            if (!this._cheatInfiniteAbility) {
                this._heroButtons[i].disable();
            }
        }

        for (const charge of this._heroCharge) {
            charge.ratio = 1;
            charge.targetRatio = 1;
            charge.speed = 0;
        }
        this._drawHeroChargeDarken();

        this._txtHeroesDamage.visible = false;

        this._hpBar.mask.clear();
        this._hpBar.mask.beginFill(0x000000);
        this._hpBar.mask.drawRect(-this._hpBar.width / 2, this._hpBar.height / 2, this._hpBar.width, -this._hpBar.height);
        this._hpBar.mask.endFill();

        this._maxHP = totalHp;
        this._displayedHP = totalHp;
        this._onSetHP(totalHp);
        this._damageAnimation.visible = false;

        for (let i = 0; i < TEAM_SIZE; ++i) {
            this._imgStunned[i].setVisible(false);
        }

        this._frameBuffAllOut.setVisible(false);
        this._frameBuffCounter.setVisible(false);
        this._frameBuffOvertime.setVisible(false);
        this._frameBuffRandomizedShooter.setVisible(false);
        this._frameBuffShield.setVisible(false);
        this._frameBuffSizeManipulation.setVisible(false);
    }

    /**
     * @private
     * @param {String | Number} positionIndex
     */
    _onInfoAbility(positionIndex) {
        const index = Game.utils.assertNumber(positionIndex, 0);

        if (Game.isPaused() && !Game.isPaused("AbilityPopup")) {
            return;
        }

        if (this._abilityButtons[index].isDisabled()) {
            return;
        }

        for (let i = 0; i < this._heroButtons.length; ++i) {
            this._heroButtons[i].disable();
        }

        Game.fire(GameEvents.eOPEN_ABILITY_POPUP, this._heroes[index], index);
    }

    /**
     * @private
     * @param {String} key
     * @param {Object} event
     */
    _onKeyDown(key, event) {
        if (key === KeyboardKey.eKEY_1) {
            if (event.shiftKey === true) {
                this._onInfoAbility(0);
            } else {
                this._onBtnUseAbility(0);
            }
        }

        if (key === KeyboardKey.eKEY_2) {
            if (event.shiftKey === true) {
                this._onInfoAbility(1);
            } else {
                this._onBtnUseAbility(1);
            }
        }

        if (key === KeyboardKey.eKEY_3) {
            if (event.shiftKey === true) {
                this._onInfoAbility(2);
            } else {
                this._onBtnUseAbility(2);
            }
        }

        if (key === KeyboardKey.eKEY_4) {
            if (event.shiftKey === true) {
                this._onInfoAbility(3);
            } else {
                this._onBtnUseAbility(3);
            }
        }
    }

    /**
     * @private
     * @param {String | Number} heroIndex
     */
    _onBtnUseAbility(heroIndex) {
        if (Game.isPaused()) return;

        const index = Game.utils.assertNumber(heroIndex, 0);

        if (this._heroButtons[index].isDisabled()) {
            return;
        }

        if (!this._heroes[index].canUseAbility()) {
            return;
        }

        const hero = this._heroes[index];
        const abilityReady = this._abilityReady[index];

        abilityReady.stop();
        abilityReady.playAnimation("end", false, { hideOnCompleted: true });
        abilityReady.onComplete = () => {
            abilityReady.visible = false;
        };

        this._cooldownLabels[index].text = hero.abilityCooldown.toString();
        this._cooldownLabels[index].visible = !this._cheatInfiniteAbility;
        this._checkmarks[index].visible = this._cheatInfiniteAbility;

        this._heroCharge[index].ratio = 1;
        this._heroCharge[index].targetRatio = 1;
        this._heroCharge[index].speed = 0;
        this._drawHeroChargeDarken();

        Common.tutorial.nextStep(Tutorials.eLVL_2);

        for (let i = 0; i < this._heroes.length; ++i) {
            if (this._heroes[i].canUseAbility()) {
                this._heroButtons[i].enable();
            }
        }

        Game.audio.playSfx("IDSND_PLAYER_ABIL_USE");

        // Delay before the cut-in to let the player enjoy the visual fx on the button
        this._timerAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.cutInDelay);
        this._timerAbilityDelay.onComplete = () => {
            Game.fire(GameEvents.eHERO_USE_ABILITY, index);
        };

        Game.fire(GameEvents.eGAME_DISABLE_INPUTS, true);
    }

    /**
     * @private
     * @param {String} heroName
     * @param {Number} charge
     * @param {Number} cooldown
     */
    _onHeroAbilityCharging(heroName, charge, cooldown) {
        const heroIndex = this._getHeroIndexByName(heroName);

        const hero = this._heroes[heroIndex];
        const lblCooldown = this._cooldownLabels[heroIndex];
        const checkmark = this._checkmarks[heroIndex];

        if (hero) {
            lblCooldown.text = (hero.abilityCooldown - hero.charge).toString();
            lblCooldown.visible = hero.abilityCooldown - hero.charge > 0;
            checkmark.visible = !lblCooldown.visible;
        } else {
            // TODO: Investigate why hero might be null
            console.warn("[HeroSideHud._onHeroAbilityCharging] hero is null : heroIndex = " + heroIndex + " : heroName = " + heroName);
        }

        const startRatio = (cooldown - (charge - 1)) / cooldown;
        const endRatio = (cooldown - charge) / cooldown;
        const abilityLine = this._abilityLine[heroIndex];
        const abilityReady = this._abilityReady[heroIndex];

        if (abilityReady.visible) {
            return;
        }

        // Begin electricity effect
        if (charge === 1) {
            abilityLine.visible = true;
            abilityLine.y = HUD_HERO_BOX_BOTTOM - HUD_HERO_BOX_HEIGHT * startRatio;
            abilityLine.playAnimation("start");

            abilityLine.onComplete = () => {
                abilityLine.playAnimation("idle", true);

                this._animateHeroCharge(heroIndex, startRatio, endRatio);
            };
        } else {
            this._animateHeroCharge(heroIndex, startRatio, endRatio);
        }
    }

    /**
     * @private
     * @param {Number} heroIndex
     * @param {Number} startRatio
     * @param {Number} endRatio
     */
    _animateHeroCharge(heroIndex, startRatio, endRatio) {
        this._heroCharge[heroIndex].ratio = startRatio;
        this._heroCharge[heroIndex].targetRatio = endRatio;
        this._heroCharge[heroIndex].speed = (endRatio - startRatio) / HUD_HERO_BOX_CHARGE_ANIMATION_DURATION;
    }

    /**
     * @private
     * @param {Number} heroIndex
     */
    _onAbilityReady(heroIndex) {
        const abilityLine = this._abilityLine[heroIndex];
        const abilityReady = this._abilityReady[heroIndex];

        // End electricity effect
        abilityLine.playAnimation("end");
        abilityLine.onComplete = () => {
            abilityLine.visible = false;

            abilityReady.visible = true;
            abilityReady.playAnimation("start");
            abilityReady.onComplete = () => {
                abilityReady.playAnimation("idle", true);
            };
        };
    }

    /**
     * @private
     */
    _drawHeroChargeDarken() {
        this._darkenMask.clear();

        for (let i = 0; i < TEAM_SIZE; ++i) {
            const imgDarken = this._imgDarkens[i];
            const charge = this._heroCharge[i];

            this._darkenMask.beginFill(0xffffff);
            this._darkenMask.drawRect(
                -HUD_HERO_BOX_WIDTH / 2,
                imgDarken.y + HUD_HERO_BOX_BOTTOM,
                HUD_HERO_BOX_WIDTH,
                -HUD_HERO_BOX_HEIGHT * charge.ratio
            );
            this._darkenMask.endFill();
        }
    }

    /**
     * @private
     * @param {String} heroName
     */
    _onHeroAbilityCharged(heroName) {
        const heroIndex = this._getHeroIndexByName(heroName);
    }

    /**
     * @private
     * @param {String} name
     * @returns {Number}
     */
    _getHeroIndexByName(name) {
        for (let i = 0; i < this._heroes.length; ++i) {
            if (this._heroes[i].name === name) {
                return i;
            }
        }
        return 0;
    }

    /**
     * @private
     * @param {Number} resultingHp
     * @param {Number} totalDamage
     * @param {EnemyParticle} particle
     */
    _onHeroesDamaged(resultingHp, totalDamage, particle) {
        this._heroesDamaged = true;
        this._heroesDamagedTimer = 0;

        if (totalDamage > 0) {
            for (let i = 0; i < TEAM_SIZE; ++i) {
                this._heroButtons[i].setSprite("FRM_HeroesBottomHurt");
                this._heroPortraits[i].setSprite(HERO_DAMAGED_SPRITES[this._heroes[i].name]);
            }
        }

        this._damageAnimation.visible = true;
        this._damageAnimation.playAnimation("hit");
        this._damageAnimation.onComplete = this._onDamageAnimationDone.bind(this);

        if (particle) {
            this._damageAnimation.x = particle.x;
            this._damageAnimation.y = particle.y;
        } else {
            this._damageAnimation.x = ENEMY_ATTACK_PARTICLE_TARGET_X;
            this._damageAnimation.y = 0;
        }

        this._txtHeroesDamage.visible = true;

        this._txtHeroesDamage.x =
            HERO_DAMAGE_LABEL_POSITION.x +
            Math.random() * Common.settings.heroDamageLabelRandomOffset * 2 -
            Common.settings.heroDamageLabelRandomOffset;

        this._txtHeroesDamage.y =
            HERO_DAMAGE_LABEL_POSITION.y +
            Math.random() * Common.settings.heroDamageLabelRandomOffset * 2 -
            Common.settings.heroDamageLabelRandomOffset;

        this._txtHeroesDamage.text = String(-totalDamage);

        this._onSetHP(resultingHp);
    }

    /**
     * @private
     * @param {Number} resultingHp
     */
    _onHealHeroes(resultingHp) {
        if (!this._healingAnimation[0].playing) {
            for (let i = 0; i < this._healingAnimation.length; ++i) {
                this._healingAnimation[i].visible = true;
                this._healingAnimation[i].playAnimation("heal");
            }
            this._healingAnimation[0].onComplete = this._onHealAnimationDone.bind(this);
        }

        this._onSetHP(resultingHp);

        Game.audio.playSfx("IDSND_PLAYER_HP_RISE");
    }

    /**
     * @private
     * @param {Number} duration
     */
    _onSetWaspBuff(duration) {
        if (!this._frameBuffSizeManipulation.visible) {
            this._activeBuffFrames.push(this._frameBuffSizeManipulation);
            this._frameBuffSizeManipulation.setVisible(true);
            this._frameBuffSizeManipulation.opacity = 0;

            const buffAppearTween = Game.windows
                .getCurrentWindow()
                .createTween(this._frameBuffSizeManipulation)
                .to({ opacity: 1 }, Common.settings.animations.buffTimer)
                .onComplete(() => this._checkBuffAppearComplete())
                .start();
            this._buffAppearTweens.push(buffAppearTween);
        }

        this._textBuffSizeManipulation.setText(duration.toString());
        this._onSetPowerUpBuff();
        this._setBuffPositions();
    }

    /**
     * @private
     */
    _onSetAllOutBuff() {
        if (!this._frameBuffAllOut.visible) {
            this._activeBuffFrames.push(this._frameBuffAllOut);
            this._frameBuffAllOut.setVisible(true);
            this._frameBuffAllOut.opacity = 0;

            const buffAppearTween = Game.windows
                .getCurrentWindow()
                .createTween(this._frameBuffAllOut)
                .to({ opacity: 1 }, Common.settings.animations.buffTimer)
                .onComplete(() => this._checkBuffAppearComplete())
                .start();
            this._buffAppearTweens.push(buffAppearTween);
        }

        this._textBuffAllOut.setText(Common.settings.allOutDamageMultiplier.toString());
        this._onSetPowerUpBuff();
        this._setBuffPositions();
    }

    /**
     * @private
     */
    _onSetPowerUpBuff() {
        for (let i = 0; i < this._powerUpBuffAnimation.length; ++i) {
            this._powerUpBuffAnimation[i].visible = true;
            this._powerUpBuffAnimation[i].playAnimation("start");
        }
        this._powerUpBuffAnimation[0].onComplete = this._onPowerUpBuffAnimationIdle.bind(this);
    }

    /**
     * @private
     */
    _onPowerUpBuffAnimationIdle() {
        for (let i = 0; i < this._powerUpBuffAnimation.length; ++i) {
            this._powerUpBuffAnimation[i].playAnimation("idle");
        }
    }

    /**
     * @private
     */
    _onPowerUpBuffAnimationDone() {
        for (let i = 0; i < this._powerUpBuffAnimation.length; ++i) {
            this._powerUpBuffAnimation[i].stop();
            this._powerUpBuffAnimation[i].visible = false;
        }
    }

    /**
     * @private
     * @param {Number} counter
     */
    _onSetRandomBubblesCounter(counter) {
        if (!this._frameBuffRandomizedShooter.visible) {
            this._activeBuffFrames.push(this._frameBuffRandomizedShooter);
            this._frameBuffRandomizedShooter.setVisible(true);
            this._frameBuffRandomizedShooter.opacity = 0;

            const buffAppearTween = Game.windows
                .getCurrentWindow()
                .createTween(this._frameBuffRandomizedShooter)
                .to({ opacity: 1 }, Common.settings.animations.buffTimer)
                .onComplete(() => this._checkBuffAppearComplete())
                .start();
            this._buffAppearTweens.push(buffAppearTween);
        }

        this._textBuffRandomizedShooter.setText(counter.toString());
        this._setBuffPositions();
    }

    /**
     * @private
     * @param {Number} turns
     */
    _onSetOvertimeTurns(turns) {
        if (!this._frameBuffOvertime.visible) {
            this._activeBuffFrames.push(this._frameBuffOvertime);
            this._frameBuffOvertime.setVisible(true);
            this._frameBuffOvertime.opacity = 0;

            const buffAppearTween = Game.windows
                .getCurrentWindow()
                .createTween(this._frameBuffOvertime)
                .to({ opacity: 1 }, Common.settings.animations.buffTimer)
                .onComplete(() => this._checkBuffAppearComplete())
                .start();
            this._buffAppearTweens.push(buffAppearTween);
        }

        this._textBuffOvertime.setText(turns.toString());
        this._setBuffPositions();
    }

    /**
     * @private
     * @param {BuffDebuff} buffName
     * @param {Number} duration
     */
    _onSetBuffTimer(buffName, duration) {
        switch (buffName) {
            case BuffDebuff.eALL_OUT:
                this._textBuffAllOut.setText(duration.toString());
                break;
            case BuffDebuff.eCOUNTER:
                this._textBuffCounter.setText(duration.toString());
                break;
            case BuffDebuff.eOVERTIME:
                this._textBuffOvertime.setText(duration.toString());
                break;
            case BuffDebuff.eRANDOMIZED_SHOOTER:
                this._textBuffRandomizedShooter.setText(duration.toString());
                break;
            case BuffDebuff.eSHIELD:
                this._textBuffShield.setText(duration.toString());
                break;
            case BuffDebuff.eSIZE_MANIPULATION:
                this._textBuffSizeManipulation.setText(duration.toString());
                break;
        }
        if (duration <= 0) {
            this._removeBuff(buffName);
        }
    }

    /**
     * @private
     * @param {BuffDebuff} buffName
     */
    _removeBuff(buffName) {
        /** @type {UiFrame[]} */
        let buffs = [];

        for (let i = 0; i < this._activeBuffFrames.length; ++i) {
            if (this._activeBuffFrames[i] === this._findBuffFromType(buffName)) {
                let removedBuff = this._activeBuffFrames[i];

                const buffDisppearTween = Game.windows
                    .getCurrentWindow()
                    .createTween(removedBuff)
                    .to({ opacity: 0 }, Common.settings.animations.buffTimer)
                    .onComplete(() => {
                        removedBuff.setVisible(false);
                        this._checkBuffDisappearComplete();
                    })
                    .start();
                this._buffDisappearTweens.push(buffDisppearTween);
            } else {
                buffs.push(this._activeBuffFrames[i]);
            }
        }

        this._activeBuffFrames = buffs;

        for (let i = 0; i < this._activeBuffFrames.length; ++i) {
            const buffMoveTween = Game.windows
                .getCurrentWindow()
                .createTween(this._activeBuffFrames[i])
                .to({ y: this._buffFramePosY[i] }, Common.settings.animations.buffTimer)
                .onComplete(() => {
                    this._checkBuffMoveComplete();
                })
                .start();
            this._buffMoveTweens.push(buffMoveTween);
        }
    }

    /**
     * @private
     * @param {BuffDebuff} buffName
     * @returns {UiFrame}
     */
    _findBuffFromType(buffName) {
        let frame = null;
        switch (buffName) {
            case BuffDebuff.eALL_OUT:
                frame = this._frameBuffAllOut;
                break;
            case BuffDebuff.eCOUNTER:
                frame = this._frameBuffCounter;
                break;
            case BuffDebuff.eOVERTIME:
                frame = this._frameBuffOvertime;
                break;
            case BuffDebuff.eRANDOMIZED_SHOOTER:
                frame = this._frameBuffRandomizedShooter;
                break;
            case BuffDebuff.eSHIELD:
                frame = this._frameBuffShield;
                break;
            case BuffDebuff.eSIZE_MANIPULATION:
                frame = this._frameBuffSizeManipulation;
                break;
        }

        return frame;
    }

    /**
     * @private
     * @param {Number} resultingHp
     */
    _onSetHP(resultingHp) {
        this._targetHP = resultingHp;
        this._heroesLblHP.text = this._targetHP.toString();
        this._txtHeroesDamage.alpha = 1;

        if (this._tweenDamageMove) {
            this._tweenDamageMove.destroy();
            this._tweenDamageMove = null;
        }

        this._tweenDamageMove = Game.windows
            .getCurrentWindow()
            .createTween(this._txtHeroesDamage)
            .to({ y: this._txtHeroesDamage.y - Common.settings.heroDamageSlideDistance }, Common.settings.heroShakeDuration * 1000)
            .easing(GameTween.Easing.QuadraticIn)
            .onComplete(() => {
                this._onDamageSlideCompleted();
            });

        this._tweenDamageMove.start();
    }

    /**
     * @private
     */
    _onDamageSlideCompleted() {
        if (this._tweenDamageMove) {
            this._tweenDamageMove.destroy();
            this._tweenDamageMove = null;
        }

        this._tweenDamageMove = Game.windows
            .getCurrentWindow()
            .createTween(this._txtHeroesDamage)
            .to(
                { y: this._txtHeroesDamage.y - Common.settings.heroDamageSlideDistance, alpha: 0 },
                (Common.settings.heroShakeDuration * 1000) / 2
            )
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._txtHeroesDamage.visible = false;

                if (this._tweenDamageMove) {
                    this._tweenDamageMove.destroy();
                    this._tweenDamageMove = null;
                }
            });

        this._tweenDamageMove.start();
    }

    /**
     * @private
     */
    _onDamageAnimationDone() {
        this._damageAnimation.visible = false;
    }

    /**
     * @private
     */
    _onHealAnimationDone() {
        for (let i = 0; i < this._healingAnimation.length; ++i) {
            this._healingAnimation[i].visible = false;
        }
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _shakeHeroes(deltaTime) {
        this._heroesDamagedTimer += deltaTime;

        if (this._captainAmericaShield.visible) {
            let shakeDistance = Common.settings.shakeDistance;
            let randomX = Math.random() * shakeDistance - shakeDistance / 2;
            let randomY = Math.random() * shakeDistance - shakeDistance / 2;
            this._captainAmericaShield.x = this._captainAmericaShieldPosition.x + randomX;
            this._captainAmericaShield.y = this._captainAmericaShieldPosition.y + randomY;
        } else {
            for (let i = 0; i < this._heroes.length; ++i) {
                let shakeDistance = Common.settings.shakeDistance;
                let randomX = Math.random() * shakeDistance - shakeDistance / 2;
                let randomY = Math.random() * shakeDistance - shakeDistance / 2;

                this._heroButtons[i].x = this._heroButtonsOriginalPos[i].x + randomX;
                this._heroButtons[i].y = this._heroButtonsOriginalPos[i].y + randomY;
            }
        }

        if (this._heroesDamagedTimer >= Common.settings.heroShakeDuration) {
            this._heroesDamaged = false;

            for (let i = 0; i < this._heroButtons.length; ++i) {
                this._heroButtons[i].x = this._heroButtonsOriginalPos[i].x;
                this._heroButtons[i].y = this._heroButtonsOriginalPos[i].y;
                if (this._targetHP > 0) {
                    this._heroButtons[i].setSprite("FRM_HeroesBottom");
                    this._heroPortraits[i].setSprite(HERO_GAMEPLAY_SPRITES[this._heroes[i].name]);
                }
            }

            this._captainAmericaShield.x = this._captainAmericaShieldPosition.x;
            this._captainAmericaShield.y = this._captainAmericaShieldPosition.y;
        }
    }

    /**
     * @private
     * @param {Number} heroId
     * @param {Number} duration
     */
    _onHeroesStunned(heroId, duration) {
        Game.audio.playSfx("IDSND_STUN_DEBUFF");
        this._imgStunned[heroId].setVisible(true);
        this._lblStunCounter[heroId].setText(duration.toString());
    }

    /**
     * @private
     * @param {Number} heroId
     * @param {Number} duration
     */
    _onRemoveStun(heroId, duration) {
        this._lblStunCounter[heroId].setText(duration.toString());

        if (duration <= 0) {
            this._imgStunned[heroId].setVisible(false);

            if (this._inputsEnabled && this._heroes[heroId].canUseAbility()) {
                this._heroButtons[heroId].enable();
            }
        }
    }

    /**
     * @private
     * @param {Boolean} isFullScreenAbility
     */
    _onEnableInputs(isFullScreenAbility = false) {
        this._inputsEnabled = true;

        if (isFullScreenAbility) {
            this._fullScreenAbilityPause = false;
        }

        if (this._fullScreenAbilityPause) {
            return;
        }

        for (let i = 0; i < this._heroes.length; ++i) {
            if (this._heroes[i].canUseAbility()) {
                this._heroButtons[i].enable();
            }
        }

        for (const abilityButton of this._abilityButtons) {
            abilityButton.enable();
        }
    }

    /**
     * @public
     * @returns {{x: Number, y: Number}}
     */
    getAbilityHandPos() {
        let heroesFrame = this._ui.findElementByName("HeroesFrame");

        for (let i = 0; i < this._heroes.length; ++i) {
            if (this._heroes[i].canUseAbility()) {
                return {
                    x: this._heroButtons[i].x + heroesFrame.x + TUTORIAL_ABILITY_HAND_OFFSET.x,
                    y: this._heroButtons[i].y + heroesFrame.y + TUTORIAL_ABILITY_HAND_OFFSET.y
                };
            }
        }

        return { x: 0, y: 0 };
    }

    /**
     * @private
     * @param {Boolean} isFullScreenAbility
     */
    _onDisableInputs(isFullScreenAbility = false) {
        this._inputsEnabled = false;

        if (isFullScreenAbility) {
            this._fullScreenAbilityPause = true;
        }

        for (let i = 0; i < this._heroes.length; ++i) {
            this._heroButtons[i].disable();
        }

        for (const abilityButton of this._abilityButtons) {
            abilityButton.disable();
        }
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateHPBar(deltaTime) {
        if (this._displayedHP > this._targetHP) {
            this._displayedHP -= Common.settings.hpPerSecond * deltaTime;
            if (this._displayedHP <= this._targetHP) {
                this._displayedHP = this._targetHP;
            }
        } else if (this._displayedHP < this._targetHP) {
            this._displayedHP += Common.settings.hpPerSecond * deltaTime;
            if (this._displayedHP >= this._targetHP) {
                this._displayedHP = this._targetHP;
            }
        }

        let hpPercentage = this._displayedHP / this._maxHP;

        this._hpBar.mask.clear();
        this._hpBar.mask.beginFill(0x000000);
        this._hpBar.mask.drawRect(-this._hpBar.width / 2, this._hpBar.height / 2, this._hpBar.width, -this._hpBar.height * hpPercentage);
        this._hpBar.mask.endFill();
    }

    /**
     * Captain America's shield appears.
     * @private
     * @param {Object} options
     */
    _onCaptainAmericaShield(options) {
        if (this._captainAmericaShield.visible) return;
        this._captainAmericaShieldAppearDisappearTime = options.shieldAppearDisappearTime;
        this._captainAmericaShieldStartSize = options.shieldStartSize;
        this._captainAmericaShieldFinalSize = options.shieldFinalSize;

        Game.audio.playSfx("IDSND_ABIL_CAPT_AMER_ACTIVATE");
        this._captainAmericaShield.setVisible(true);
        this._captainAmericaShield.setOpacity(0);
        this._captainAmericaShield.setScale(this._captainAmericaShieldStartSize, this._captainAmericaShieldStartSize);

        this._tweenCaptainAmericaShield = Game.windows
            .getCurrentWindow()
            .createTween(this._captainAmericaShield)
            .to({ scale: this._captainAmericaShieldFinalSize, opacity: 1 }, this._captainAmericaShieldAppearDisappearTime)
            .onComplete(() => {
                if (this._tweenCaptainAmericaShield) {
                    this._tweenCaptainAmericaShield.destroy();
                    this._tweenCaptainAmericaShield = null;
                }
            })
            .start();

        if (!this._frameBuffShield.visible) {
            this._activeBuffFrames.push(this._frameBuffShield);
            this._frameBuffShield.setVisible(true);
            this._frameBuffShield.opacity = 0;

            const buffAppearTween = Game.windows
                .getCurrentWindow()
                .createTween(this._frameBuffShield)
                .to({ opacity: 1 }, Common.settings.animations.buffTimer)
                .onComplete(() => this._checkBuffAppearComplete())
                .start();
            this._buffAppearTweens.push(buffAppearTween);
        }

        this._textBuffShield.setText(options.abilityDuration.toString());

        this._setBuffPositions();
    }

    /**
     * Captain America's shield disappears.
     * @private
     */
    _onCaptainAmericaShieldDone() {
        if (!this._captainAmericaShield.visible) return;

        this._tweenCaptainAmericaShield = Game.windows
            .getCurrentWindow()
            .createTween(this._captainAmericaShield)
            .to({ scale: this._captainAmericaShieldStartSize, opacity: 0 }, this._captainAmericaShieldAppearDisappearTime)
            .onComplete(() => {
                this._captainAmericaShield.setVisible(false);

                if (this._tweenCaptainAmericaShield) {
                    this._tweenCaptainAmericaShield.destroy();
                    this._tweenCaptainAmericaShield = null;
                }
            })
            .start();
    }

    /**
     * Black Panther's Thorns aura appears.
     * @private
     * @param {Number} abilityDamageReduction
     * @param {Number} abilityDuration
     */
    _onSpawnCounter(abilityDamageReduction, abilityDuration) {
        if (!this._frameBuffCounter.visible) {
            this._activeBuffFrames.push(this._frameBuffCounter);
            this._frameBuffCounter.setVisible(true);
            this._frameBuffCounter.opacity = 0;

            const buffAppearTween = Game.windows
                .getCurrentWindow()
                .createTween(this._frameBuffCounter)
                .to({ opacity: 1 }, Common.settings.animations.buffTimer)
                .onComplete(() => this._checkBuffAppearComplete())
                .start();
            this._buffAppearTweens.push(buffAppearTween);
        }
        this._textBuffCounter.setText(abilityDuration.toString());

        this._setBuffPositions();
    }

    /**
     * @private
     */
    _setBuffPositions() {
        for (let i = 0; i < this._activeBuffFrames.length; ++i) {
            this._activeBuffFrames[i].setPosition(0, this._buffFramePosY[i]);
        }
    }

    /**
     * @private
     */
    _checkBuffAppearComplete() {
        let isPlaying = false;
        for (const tween of this._buffAppearTweens) {
            if (tween.isPlaying()) {
                isPlaying = true;
            }
        }

        if (!isPlaying) {
            for (const tween of this._buffAppearTweens) {
                tween.destroy();
            }
            this._buffAppearTweens = [];
        }
    }

    /**
     * @private
     */
    _checkBuffDisappearComplete() {
        let isPlaying = false;
        for (const tween of this._buffDisappearTweens) {
            if (tween.isPlaying()) {
                isPlaying = true;
            }
        }

        if (!isPlaying) {
            for (const tween of this._buffDisappearTweens) {
                tween.destroy();
            }
            this._buffDisappearTweens = [];
        }
    }

    /**
     * @private
     */
    _checkBuffMoveComplete() {
        let isPlaying = false;
        for (const tween of this._buffMoveTweens) {
            if (tween.isPlaying()) {
                isPlaying = true;
            }
        }

        if (!isPlaying) {
            for (const tween of this._buffMoveTweens) {
                tween.destroy();
            }
            this._buffMoveTweens = [];
        }
    }

    /**
     * @private
     * @param {Boolean} enabled
     */
    _onCheatsAvengersInfiniteAbility(enabled) {
        this._cheatInfiniteAbility = enabled;
    }

    /**
     * @private
     */
    _destroyBuffTweens() {
        for (const tween of this._buffMoveTweens) {
            tween.destroy();
        }
        this._buffMoveTweens = [];

        for (const tween of this._buffAppearTweens) {
            tween.destroy();
        }
        this._buffAppearTweens = [];

        for (const tween of this._buffDisappearTweens) {
            tween.destroy();
        }
        this._buffDisappearTweens = [];
    }

    /**
     * @private
     */
    _onHighlightHeroCounters() {
        for (const counterHighlight of this._counterHighlights) {
            counterHighlight.visible = true;
        }
    }

    /**
     * @private
     */
    _onHighlightHeroAbilities() {
        for (const abilityHighlight of this._abilityHighlights) {
            abilityHighlight.visible = true;
        }
    }

    /**
     * @private
     */
    _onHighlightClear() {
        for (const counterHighlight of this._counterHighlights) {
            counterHighlight.visible = false;
        }

        for (const abilityHighlight of this._abilityHighlights) {
            abilityHighlight.visible = false;
        }
    }

    /**
     * @public
     */
    closeLevel() {
        this._activeBuffFrames = [];
        this._destroyBuffTweens();

        if (this._tweenDamageMove) {
            this._tweenDamageMove.destroy();
            this._tweenDamageMove = null;
        }

        if (this._timerAbilityDelay) {
            this._timerAbilityDelay.destroy();
            this._timerAbilityDelay = null;
        }

        if (this._tweenCaptainAmericaShield) {
            this._tweenCaptainAmericaShield.destroy();
            this._tweenCaptainAmericaShield = null;
        }

        this._onHighlightClear();

        Game.off(GameEvents.eHEROES_INITIALIZED, this._onHeroesInitialized, this);

        Game.off(GameEvents.eHERO_BUTTON_USE_ABILITY, this._onBtnUseAbility, this);
        Game.off(GameEvents.eHERO_INFO_ABILITY, this._onInfoAbility, this);
        Game.off(GameEvents.eHERO_ABILITY_CHARGING, this._onHeroAbilityCharging, this);
        Game.off(GameEvents.eHERO_ABILITY_CHARGED, this._onHeroAbilityCharged, this);
        Game.off(GameEvents.eHEROES_DAMAGED, this._onHeroesDamaged, this);
        Game.off(GameEvents.eHEROES_HEALED, this._onHealHeroes, this);
        Game.off(GameEvents.eHEROES_STUNNED_VISUAL, this._onHeroesStunned, this);
        Game.off(GameEvents.eHEROES_REMOVE_STUN, this._onRemoveStun, this);
        Game.off(GameEvents.eGAME_ENABLE_INPUTS, this._onEnableInputs, this);
        Game.off(GameEvents.eGAME_DISABLE_INPUTS, this._onDisableInputs, this);
        Game.off(GameEvents.eHERO_ABILITY_CAPTAIN_AMERICA, this._onCaptainAmericaShield, this);
        Game.off(GameEvents.eHERO_ABILITY_CAPTAIN_AMERICA_DONE, this._onCaptainAmericaShieldDone, this);
        Game.off(GameEvents.eHEROES_ALL_OUT_ATTACK_CUTIN_DONE, this._onSetAllOutBuff, this);
        Game.off(GameEvents.eHERO_ABILITY_WASP, this._onSetWaspBuff, this);
        Game.off(GameEvents.eHERO_ABILITY_BLACK_PANTHER_ACTIVATE, this._onSpawnCounter, this);
        Game.off(GameEvents.eGAME_STOP_POWER_UP_BUFF_ANIMATION, this._onPowerUpBuffAnimationDone, this);
        Game.off(GameEvents.eGAME_SET_NUMBER_OVERTIME_TURNS, this._onSetOvertimeTurns, this);
        Game.off(GameEvents.eGAME_SET_RANDOM_BUBBLES_COUNTER, this._onSetRandomBubblesCounter, this);
        Game.off(GameEvents.eGAME_SET_BUFF_TIMER, this._onSetBuffTimer, this);
        Game.off(CoreEvents.eINPUT_KEY_DOWN, this._onKeyDown, this);

        Game.off(GameEvents.eTUTORIAL_HIGHLIGHT_HERO_COUNTERS, this._onHighlightHeroCounters, this);
        Game.off(GameEvents.eTUTORIAL_HIGHLIGHT_HERO_ABILITIES, this._onHighlightHeroAbilities, this);
        Game.off(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR, this._onHighlightClear, this);

        Game.off(CheatEvents.eAVENGERS_INFINITE_ABILITY, this._onCheatsAvengersInfiniteAbility, this);
    }
}
